import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Autosuggest from 'react-autosuggest';
import { TouchableWithoutFeedback } from 'react-native';
import { Inline } from '@styleseat/ui';
import { BrandColor } from '@styleseat/brand';
import store from '../../../../store';
import Janitor from '../../../../modules/Janitor';
import debouncer from '../../../../modules/debouncer';
import analytics from '../../../../modules/analytics';
import fetchSuggestions from '../../../../modules/consumer/search/Autocomplete';
import toHighlightedParts from '../toHighlightedParts';
import { getIOSVersion } from '../../../../modules/AppInfo';
import {
  BodyText,
  HeaderText,
  Button,
} from '../../../ui';
import { BasicSearchIcon } from '../../../ui/Icons';
import './QueryFilter.scss';

const SERVICE_SECTION = 0;
const PRO_SECTION = 1;
const SALON_SECTION = 2;

/**
 * Given an autocomplete suggestion, return the suggestion text, including any areas that need to
 * be emphasized, as a react element.
 * @param {Object} suggestion - The search autocomplete suggestion item.
 * @return {ReactComponent} The formatted text.
 */
function formatSuggestion(suggestion) {
  const matches = toHighlightedParts(suggestion.text, suggestion.matchedSubstrings);
  return matches.map(({
    text,
    isMatch,
    idx,
  }) => (
    <React.Fragment key={idx}>
      {isMatch ? <BodyText weight="bold">{text}</BodyText> : text}
    </React.Fragment>
  ));
}

/**
 * Return what the input field value should be for an autocomplete suggestion.
 */
function getSuggestionValue(suggestion) {
  return suggestion.text;
}

/**
 * The query picker popover for search.
 *
 * It includes an autocomplete for query suggestions.
 *
 * The query suggestion the user selects gets sent to the SearchConfig redux state.
 *
 * @example
 * import { ConnectedQueryFilter } from 'components/consumer/search/QueryFilter';
 *
 * const MyComponent = () => {
 *   return (
 *     <ConnectedQueryFilter />
 *   );
 * }
 */
export class QueryFilter extends React.Component {
  static propTypes = {
    query: PropTypes.string,
    setQuery: PropTypes.func,
    onOpenPopover: PropTypes.func,
    onClosePopover: PropTypes.func,
    nextSearchID: PropTypes.string,
    /** set query on focus lost of input if we are not on the search page so we can actually make a
     * query when ready */
    setQueryOnFocusLost: PropTypes.bool,
    /** whether to show the h1 or not */
    showHeaderText: PropTypes.bool,
    /** True to show the search button, otherwise false */
    showSearchButton: PropTypes.bool,
    /** True to hide the search button on the focused view */
    hideSearchButtonInFocusedView: PropTypes.bool,
    /** True to show the magnifying glass icon, otherwise false */
    showIcon: PropTypes.bool,
    /** For the mobile search test (mobile_header_search_20200129) */
    appearance: PropTypes.oneOf([
      'default',
      'mobile-search',
      'rounded',
    ]),
    renderHeader: PropTypes.func,
    renderInputComponent: PropTypes.func,
    style: PropTypes.object,
    /** Called when the search button is clicked, or an auto suggest entry is selected */
    onSearch: PropTypes.func,
    tracking: PropTypes.object,
    /** Called when the input field loses focus */
    onBlur: PropTypes.func,
  };

  static defaultProps = {
    query: '',
    onOpenPopover: null,
    onClosePopover: null,
    nextSearchID: null,
    setQueryOnFocusLost: false,
    showHeaderText: true,
    showSearchButton: true,
    showIcon: true,
    appearance: 'default',
    renderHeader: null,
    renderInputComponent: null,
    style: {},
    hideSearchButtonInFocusedView: false,
    onSearch: () => { },
    setQuery: () => { },
    tracking: {},
    onBlur: null,
  };

  text = '';

  janitor = new Janitor();

  apiDebounce = debouncer();

  autosuggestComponent = React.createRef();

  constructor(props) {
    super(props);
    const initialText = props.query || '';

    // since set state is asnychronous we cannot rely on this.state
    // having the correct values so each time we call setState({ text })
    // we need to also update the reference on this.
    this.text = initialText;

    // make sure initial text matches query prop
    // initializing state here because we may not have final prop values elsewhere
    this.state = {
      text: initialText,
      lastTypedText: initialText,
      // used to monitor whether the user has explicitly changed the search query from what was
      // passed as a prop
      queryHasChanged: false,
      suggestions: [],
      focused: false,
      highlightedSuggestion: null,
    };
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.query !== prevProps.query) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ text: this.props.query || '' });
    }

    if (!document.querySelector('.main-content')) return;

    if (this.state.focused !== prevState.focused) {
      if (this.state.focused) {
        analytics.track('client_search_filter_focused', this.props.tracking);
      }
      // show-query-filter is used in _responsive_base.scss to control the z-index of the
      // main-content class
      if (this.state.focused && this.props.appearance === 'default') {
        document.querySelector('.main-content')
          .classList
          .add('show-query-filter');
      } else {
        document.querySelector('.main-content')
          .classList
          .remove('show-query-filter');
      }
    }
  }

  componentWillUnmount() {
    this.apiDebounce.stop();
    this.janitor.cleanup();
  }

  /**
   * A search suggestion was selected.
   * See: https://github.com/moroshko/react-autosuggest#on-suggestion-selected-prop
   * @param {Event} _
   * @param {Object} suggestion
   */
  handleSuggestionSelected = (_, {
    suggestion,
    sectionIndex,
  }) => {
    const { nextSearchID } = this.props;

    analytics.track('client_suggestion_selected', {
      search_id: nextSearchID,
      suggestion_text: suggestion.text,
      section_index: sectionIndex,
    });

    if (sectionIndex === PRO_SECTION) {
      // Navigate to provider
      store.dispatch.route.go({
        route: 'profile',
        params: { providerId: suggestion.vanity_url, proId: undefined },
      });
    } else if (sectionIndex === SALON_SECTION) {
      // Navigate to salon
      store.dispatch.route.go({
        route: 'salon',
        params: { salonId: suggestion.id },
      });
    } else {
      // Update search filter
      const text = getSuggestionValue(suggestion);
      this.text = text;
      this.props.setQuery(text);
      this.props.onSearch(text);
      this.setState({
        focused: false,
        text,
      });
    }

    this.handleClearSuggestions();
  };

  /**
   * Handles the onSuggestionHighlighted event.
   * @see https://github.com/moroshko/react-autosuggest#onsuggestionhighlighted-optional
   */
  handleSuggestionHighlighted = ({ suggestion }) => {
    this.setState({ highlightedSuggestion: suggestion });
  };

  /**
   * Clear all the suggestions.
   */
  handleClearSuggestions = () => {
    this.setState({
      suggestions: [],
    });
  };

  /**
   * Clear search text.
   */
  handleClearClick = () => {
    if (this.state.focused) {
      // clear the text
      this.text = '';
      this.setState({
        text: '',
        queryHasChanged: true,
        focused: true,
      });
    } else {
      this.setState({
        focused: true,
      });
    }

    // maintain focus on the input, as clearing text is effectively "typing"
    if (this.autosuggestComponent.current) {
      this.autosuggestComponent.current.input.focus();
    }
  };

  /**
   * Query the search API to get the service / salon / pro autocomplete suggestions and transform
   * the response into a standard form placed in the `suggestions` state variable.
   */
  handleLoadSuggestions = ({ value }) => {
    if (value) {
      this.apiDebounce(() => {
        this.janitor.addPromise(fetchSuggestions(value))
          .then(data => {
            const suggestions = [
              {
                title: 'Services',
                suggestions: data.services.map((service, i) => ({
                  text: service.search_query.query,
                  matchedSubstrings: service.matchedSubstrings,
                  section: SERVICE_SECTION,
                  testId: `search-queryfilter-service-suggestion-${i}`,
                })),
              },
              {
                title: 'Stylists',
                suggestions: data.pros.map((pro, i) => ({
                  text: pro.name,
                  id: pro.id,
                  testId: `search-queryfilter-pro-suggestion-${i}`,
                  vanity_url: pro.vanity_url,
                  matchedSubstrings: pro.matchedSubstrings,
                  section: PRO_SECTION,
                })),
              },
              {
                title: 'Salons',
                suggestions: data.salons.map((salon, i) => ({
                  text: salon.name,
                  id: salon.id,
                  testId: `search-queryfilter-salon-suggestion-${i}`,
                  matchedSubstrings: salon.matchedSubstrings,
                  section: SALON_SECTION,
                })),
              },
            ];

            this.setState({
              suggestions,
            });
          });
      });
    } else {
      this.handleClearSuggestions();
    }
  };

  /**
   * Called when the value of the location field changes.
   * See: https://github.com/moroshko/react-autosuggest#input-props-prop
   */
  handleFieldChange = (_, {
    newValue,
    method,
  }) => {
    let {
      lastTypedText,
      text,
      queryHasChanged,
    } = this.state;

    // keep track of the last typed text
    if (method === 'type') {
      lastTypedText = newValue;
    }

    if (method !== 'escape') {
      text = newValue;
      queryHasChanged = true;
    }

    this.text = text;
    this.setState({
      lastTypedText,
      text,
      queryHasChanged,
    });
  };

  handleFieldKeyPress = event => {
    const queryValue = event.target.value;
    const isServiceOrText = !this.state.highlightedSuggestion
      || this.state.highlightedSuggestion.section === SERVICE_SECTION;

    // if someone types and hits the "enter" key, use the suggestion they highlighted or the text
    // they just entered. If a pro or salon is highlighted, we want to let the suggestion selection
    // handler execute
    if (event.key === 'Enter' && isServiceOrText) {
      event.stopPropagation();
      this.props.setQuery(queryValue || '');
      this.props.onSearch(queryValue || '');
      this.closePopover();
    }
  };

  closePopover = () => {
    const { onClosePopover } = this.props;

    // make sure the search field is no longer focused
    if (this.autosuggestComponent.current) {
      this.autosuggestComponent.current.input.blur();
    }

    this.handleClearSuggestions();

    if (typeof onClosePopover === 'function') {
      onClosePopover(this.state.query);
    }
  };

  handleFieldGotFocus = evt => {
    const { onOpenPopover } = this.props;

    evt.target.select();

    this.setState({ focused: true }, () => {
      // Fix issue with keyboard pushing the field off the top of the page. (bug: BB-2428)
      // I hate verything about this fix.
      if (getIOSVersion()) {
        setTimeout(() => {
          window.scrollTo(0, 0);
        }, 500);
      }
    });

    if (typeof onOpenPopover === 'function') {
      onOpenPopover();
    }
  };

  handleFieldLostFocus = event => {
    const {
      onClosePopover,
      onBlur,
    } = this.props;

    this.setState({ focused: false });

    if (this.props.setQueryOnFocusLost) {
      this.props.setQuery(this.text);
    }

    if (typeof onClosePopover === 'function') {
      onClosePopover(this.text);
    }

    onBlur?.(event);
  };

  /**
   * Handles a click on the "Search" button
   */
  handleSearchClick = () => {
    const { text } = this;
    const { nextSearchID } = this.props;

    analytics.track('client_search_clicked', {
      search_id: nextSearchID,
      service_search_text: text,
    });

    this.props.setQuery(text || '');
    this.props.onSearch(text || '');
    this.closePopover();
  };

  getShowSearchButton = () => {
    const { focused } = this.state;
    const {
      showSearchButton,
      hideSearchButtonInFocusedView,
    } = this.props;

    if (showSearchButton) {
      if (!focused) {
        return true;
      }
      return !hideSearchButtonInFocusedView;
    }
    return false;
  };

  /**
   * Render the header element, which will display the input box and search button.
   */
  renderInputComponent = inputProps => {
    const {
      showHeaderText,
      showSearchButton,
      showIcon,
      renderHeader,
      renderInputComponent,
      appearance,
    } = this.props;

    const withBorderRadius = appearance === 'rounded';

    if (renderInputComponent) {
      return renderInputComponent({
        ...inputProps,
        showHeaderText,
        showSearchButton,
        showIcon,
        renderHeader,
      });
    }

    return (
      <header>
        {showHeaderText && (<h2>Search</h2>)}
        {typeof renderHeader === 'function' && renderHeader()}
        <section
          className={classNames(
            'search-input',
            {
              'show-icon': showIcon,
              'show-search-button': showSearchButton,
              'with-border-radius': withBorderRadius,
              'without-search-input-border': withBorderRadius,
            },
          )}
        >
          <input {...inputProps} />
          {this.getShowSearchButton() && (
            <Button
              size="large"
              appearance="primary"
              testID="query-filter-search"
              style={{
                display: 'flex',
                padding: 8,
                height: withBorderRadius ? 40 : 45,
                borderRadius: withBorderRadius ? 28 : undefined,
              }}
              onClick={this.handleSearchClick}
            >
              <BasicSearchIcon color={BrandColor.WhiteWash} />
            </Button>
          )}
        </section>
      </header>

    );
  };

  /**
   * Render an individual search suggestion.
   */
  renderSuggestion = (suggestion, { isHighlighted }) => (
    <TouchableWithoutFeedback>
      <Inline
        paddingY={12}
        paddingX={40}
        color={isHighlighted ? BrandColor.Chill : BrandColor.WhiteWash}
        borderBottomColor={BrandColor.LightGrey}
        borderBottomWidth={1}
        borderStyle="solid"
        testID={suggestion.testId}
      >
        <BodyText>{formatSuggestion(suggestion)}</BodyText>
      </Inline>
    </TouchableWithoutFeedback>
  );

  /**
   * Render the header of a section of search suggestions.
   * @see https://github.com/moroshko/react-autosuggest#render-section-title-prop
   */
  renderSectionTitle = section => {
    // only display a section header if the section is not empty
    if (section.suggestions.length) {
      return (
        <Inline
          alignItems="center"
          paddingX={24}
          paddingY={12}
          borderBottomColor={BrandColor.LightGrey}
          borderBottomWidth={1}
          borderStyle="solid"
        >
          <HeaderText level={4} textTransform="uppercase">{section.title}</HeaderText>
        </Inline>
      );
    }

    return '';
  };

  /**
   * Retrives the suggestions for a given section of the autosuggest. This will be a list of pros,
   * salons, or services.
   * @see https://github.com/moroshko/react-autosuggest#get-section-suggestions-prop
   */
  getSectionSuggestions = section => section.suggestions;

  render() {
    const {
      query,
      appearance,
      style,
    } = this.props;
    const {
      text,
      suggestions,
      focused,
      queryHasChanged,
    } = this.state;

    // do we want loading text or error text?
    return (
      <section
        className={classNames(
          'query-filter-component',
          {
            focused,
            'show-mobile-appearance': appearance === 'mobile-search',
          },
        )}
        style={style}
      >
        <Autosuggest
          multiSection
          inputProps={{
            placeholder: 'Service, Stylist or Salon',
            value: queryHasChanged ? text : query,
            onChange: this.handleFieldChange,
            onFocus: this.handleFieldGotFocus,
            onBlur: this.handleFieldLostFocus,
            onKeyPress: this.handleFieldKeyPress,
            'data-testid': 'query-input',
          }}
          focusInputOnSuggestionClick={false}
          suggestions={suggestions}
          onSuggestionsFetchRequested={this.handleLoadSuggestions}
          onSuggestionsClearRequested={this.handleClearSuggestions}
          onSuggestionSelected={this.handleSuggestionSelected}
          onSuggestionHighlighted={this.handleSuggestionHighlighted}
          getSuggestionValue={getSuggestionValue}
          renderSuggestion={this.renderSuggestion}
          getSectionSuggestions={this.getSectionSuggestions}
          renderSectionTitle={this.renderSectionTitle}
          renderInputComponent={this.renderInputComponent}
          ref={this.autosuggestComponent}
        />
      </section>
    );
  }
}

export default QueryFilter;
